// FILE: apps/admin/src/pages/SupportTickets.tsx
// VERSION: 2.1.0
// START_MODULE_CONTRACT
//   PURPOSE: Unified Disputes & Support Desk ("Жалобы и споры") with responsive mobile-cards layout combining Mini App Player Reports moderation (snapshot vs live nickname/avatar + 1-click actions) and Telegram Bot /support ticket conversations.
//   SCOPE: SupportTickets component.
//   DEPENDS: ../api.js, ../components/AttachmentGallery.js, ../components/Icons.js, ../components/UserAvatar.js, ../toast.js
//   LINKS: M-ADMIN
//   ROLE: RUNTIME
//   MAP_MODE: EXPORTS
// END_MODULE_CONTRACT
//
// START_MODULE_MAP
//   SupportTickets - Dual-mode Player Reports moderation queue and /support ticket conversation console
// END_MODULE_MAP

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { api, dt, num } from '../api.js';
import {
  AttachmentCountBadge,
  AttachmentGallery,
  type MediaAttachmentItem,
} from '../components/AttachmentGallery.js';
import { AdminIcon } from '../components/Icons.js';
import { UserAvatar } from '../components/UserAvatar.js';
import { useToast } from '../toast.js';

interface SupportMessageRow {
  id: string;
  authorRole: 'USER' | 'OPERATOR' | 'INTERNAL_NOTE';
  authorDisplayName: string;
  body: string;
  createdAt: string;
}

interface AdminSupportTicketDto {
  id: string;
  userId: string;
  telegramId: number;
  username: string | null;
  firstName: string | null;
  category: string;
  subject: string;
  state: string;
  filesCount?: number;
  lastMessageAt: string;
  messages?: SupportMessageRow[];
  attachments?: MediaAttachmentItem[];
}

interface TicketsListResponse {
  items: AdminSupportTicketDto[];
  openCount: number;
}

interface AdminUserReportDto {
  id: string;
  reportNumber: number;
  reporterUserId: string;
  reporterTelegramId: number | null;
  reporterUsername: string | null;
  reporterDisplayName: string | null;
  targetUserId: string;
  targetTelegramId: number | null;
  targetUsername: string | null;
  targetFirstName: string | null;
  targetCustomName: string | null;
  targetPhotoUrl: string | null;
  targetCustomPhotoUrl: string | null;
  targetIsBanned: boolean;
  context: string;
  reason: string;
  comment: string | null;
  snapshotName: string | null;
  snapshotUsername: string | null;
  snapshotPhotoUrl: string | null;
  state: 'OPEN' | 'RESOLVED' | 'DISMISSED';
  resolutionAction: string | null;
  resolutionNote: string | null;
  resolvedByTelegramId: number | null;
  resolvedAt: string | null;
  createdAt: string;
  attachments?: MediaAttachmentItem[];
}

interface UserReportsListResponse {
  items: AdminUserReportDto[];
  openCount: number;
}

type ReportResolutionAction =
  | 'RESET_NAME'
  | 'RESET_AVATAR'
  | 'RESET_BOTH'
  | 'BAN_USER'
  | 'DISMISS';

const TICKET_STATES = ['ALL', 'OPEN', 'IN_PROGRESS', 'WAITING_USER', 'RESOLVED', 'CLOSED'] as const;
const CATEGORIES = ['ALL', 'DELIVERY', 'PAYMENT', 'GAMES', 'FISH', 'ACCOUNT', 'OTHER'] as const;
const REPORT_STATES = ['ALL', 'OPEN', 'RESOLVED', 'DISMISSED'] as const;

const REASON_LABELS: Record<string, string> = {
  inappropriate_name: 'Некорректный ник',
  inappropriate_avatar: 'Некорректная аватарка',
  abuse_spam: 'Оскорбления / спам',
  scam: 'Мошенничество / фейк',
};

const CONTEXT_LABELS: Record<string, string> = {
  leaderboard: 'Лидерборд',
  crash: 'Игра Crash',
  profile: 'Профиль',
};

// START_CONTRACT: SupportTickets
//   PURPOSE: Render the Disputes & Support Desk with "Жалобы на игроков" moderation queue and "Тикеты /support" chat console.
//   INPUTS: none
//   OUTPUTS: React element
//   SIDE_EFFECTS: Queries and mutates /admin/support/reports and /admin/support/tickets
//   LINKS: M-ADMIN
// END_CONTRACT: SupportTickets
export function SupportTickets() {
  // START_BLOCK_SUPPORT_TICKETS
  const [deskMode, setDeskMode] = useState<'reports' | 'tickets'>('reports');
  const navigate = useNavigate();

  // Reports state
  const [reportStateFilter, setReportStateFilter] = useState<string>('ALL');
  const [reportQ, setReportQ] = useState('');
  const [activeReportId, setActiveReportId] = useState<string | null>(null);
  const [reportNote, setReportNote] = useState('');

  // Tickets state
  const [stateFilter, setStateFilter] = useState<string>('ALL');
  const [categoryFilter, setCategoryFilter] = useState<string>('ALL');
  const [q, setQ] = useState('');
  const [activeId, setActiveId] = useState<string | null>(null);
  const [replyText, setReplyText] = useState('');
  const [internalNote, setInternalNote] = useState('');
  const [nextState, setNextState] = useState<
    'OPEN' | 'IN_PROGRESS' | 'WAITING_USER' | 'RESOLVED' | 'CLOSED'
  >('WAITING_USER');

  const qc = useQueryClient();
  const toast = useToast();

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const tid = searchParams.get('ticketId');
    if (tid) {
      setDeskMode('tickets');
      setActiveId(tid);
      return;
    }
    const m = window.location.hash.match(/[?&]ticketId=([^&]+)/);
    if (m?.[1]) {
      setDeskMode('tickets');
      setActiveId(decodeURIComponent(m[1]));
    }
  }, []);

  const { data: reportsData, isLoading: reportsLoading } = useQuery({
    queryKey: ['support-reports', reportStateFilter, reportQ],
    queryFn: () =>
      api.get<UserReportsListResponse>(
        `/admin/support/reports?state=${encodeURIComponent(reportStateFilter)}&q=${encodeURIComponent(reportQ)}`,
      ),
    refetchInterval: 12000,
  });

  const { data, isLoading } = useQuery({
    queryKey: ['support-tickets', stateFilter, categoryFilter, q],
    queryFn: () =>
      api.get<TicketsListResponse>(
        `/admin/support/tickets?state=${encodeURIComponent(stateFilter)}&category=${encodeURIComponent(categoryFilter)}&q=${encodeURIComponent(q)}`,
      ),
    refetchInterval: 12000,
  });

  const { data: ticketDetail } = useQuery({
    queryKey: ['support-ticket', activeId],
    queryFn: () => api.get<AdminSupportTicketDto>(`/admin/support/tickets/${activeId}`),
    enabled: deskMode === 'tickets' && Boolean(activeId),
    refetchInterval: 8000,
  });

  const resolveReportMut = useMutation({
    mutationFn: ({ id, action }: { id: string; action: ReportResolutionAction }) =>
      api.post(`/admin/support/reports/${id}/resolve`, {
        action,
        note: reportNote.trim() || undefined,
      }),
    onSuccess: () => {
      toast('Жалоба обработана');
      setReportNote('');
      void qc.invalidateQueries({ queryKey: ['support-reports'] });
      void qc.invalidateQueries({ queryKey: ['users'] });
      void qc.invalidateQueries({ queryKey: ['dashboard'] });
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'Ошибка обработки жалобы'),
  });

  const replyMut = useMutation({
    mutationFn: () =>
      api.post(`/admin/support/tickets/${activeId}/reply`, {
        message: replyText.trim() || undefined,
        internalNote: internalNote.trim() || undefined,
        nextState,
      }),
    onSuccess: () => {
      toast('Ответ сохранён и отправлен пользователю в Telegram');
      setReplyText('');
      setInternalNote('');
      void qc.invalidateQueries({ queryKey: ['support-ticket', activeId] });
      void qc.invalidateQueries({ queryKey: ['support-tickets'] });
      void qc.invalidateQueries({ queryKey: ['dashboard'] });
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'Ошибка отправки ответа'),
  });

  const activeReport =
    (reportsData?.items ?? []).find((r) => r.id === activeReportId) ??
    (reportsData?.items ?? [])[0] ??
    null;

  return (
    <>
      <div className="page-header">
        <div>
          <h1>Жалобы и споры</h1>
          <div className="page-subtitle">
            Модерация жалоб на игроков из Mini App и обращения в поддержку (/support) · Открытых жалоб:{' '}
            <strong>{num(reportsData?.openCount ?? 0)}</strong> · Открытых тикетов:{' '}
            <strong>{num(data?.openCount ?? 0)}</strong>
          </div>
        </div>

        <div className="tabs" style={{ marginBottom: 0, borderBottom: 'none', paddingBottom: 0 }}>
          <button
            type="button"
            className={`tab-btn ${deskMode === 'reports' ? 'active' : ''}`}
            onClick={() => setDeskMode('reports')}
          >
            <AdminIcon name="flag" size={13} style={{ marginRight: 6 }} />
            Жалобы на игроков ({num(reportsData?.openCount ?? 0)})
          </button>
          <button
            type="button"
            className={`tab-btn ${deskMode === 'tickets' ? 'active' : ''}`}
            onClick={() => setDeskMode('tickets')}
          >
            Тикеты /support ({num(data?.openCount ?? 0)})
          </button>
        </div>
      </div>

      {deskMode === 'reports' ? (
        <>
          <div className="toolbar">
            <div className="tabs" style={{ marginBottom: 0 }}>
              {REPORT_STATES.map((s) => (
                <button
                  key={s}
                  type="button"
                  className={`tab-btn ${reportStateFilter === s ? 'active' : ''}`}
                  onClick={() => setReportStateFilter(s)}
                >
                  {s === 'ALL'
                    ? 'Все жалобы'
                    : s === 'OPEN'
                      ? 'Открытые'
                      : s === 'RESOLVED'
                        ? 'Приняты меры'
                        : 'Отклонённые'}
                </button>
              ))}
            </div>
            <input
              style={{ flex: '1 1 220px', minWidth: 0 }}
              placeholder="Поиск по нику, @username, комментарию…"
              value={reportQ}
              onChange={(e) => setReportQ(e.target.value)}
            />
          </div>

          <div className="grid cols-2" style={{ alignItems: 'start' }}>
            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
              {reportsLoading ? (
                <div className="muted" style={{ padding: 20 }}>
                  Загрузка очереди жалоб…
                </div>
              ) : (
                <div className="table-wrap">
                  <table className="mobile-cards">
                    <thead>
                      <tr>
                        <th>На кого жалоба</th>
                        <th>Причина</th>
                        <th>Статус</th>
                        <th>Дата</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(reportsData?.items ?? []).map((r) => {
                        const displayTarget =
                          r.snapshotName || r.targetCustomName || r.targetFirstName || 'Пользователь';
                        return (
                          <tr
                            key={r.id}
                            style={{
                              cursor: 'pointer',
                              background: activeReport?.id === r.id ? 'var(--accent-soft)' : undefined,
                            }}
                            onClick={() => setActiveReportId(r.id)}
                          >
                            <td className="mobile-card-primary" data-label="На кого жалоба">
                              <div className="user-cell">
                                <UserAvatar
                                  photoUrl={r.snapshotPhotoUrl ?? r.targetPhotoUrl}
                                  customPhotoUrl={r.targetCustomPhotoUrl}
                                  name={displayTarget}
                                  size={32}
                                />
                                <div>
                                  <div style={{ fontWeight: 600 }}>{displayTarget}</div>
                                  <div className="muted mono" style={{ fontSize: 11 }}>
                                    #{r.reportNumber} ·{' '}
                                    {r.targetUsername ? `@${r.targetUsername}` : `#${r.targetTelegramId ?? ''}`}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td data-label="Причина">
                              <div style={{ fontWeight: 500 }}>
                                {REASON_LABELS[r.reason] ?? r.reason}
                              </div>
                              <div>
                                <span className="chip info" style={{ marginTop: 4 }}>
                                  {CONTEXT_LABELS[r.context] ?? r.context}
                                </span>
                                <AttachmentCountBadge attachments={r.attachments} />
                              </div>
                            </td>
                            <td data-label="Статус">
                              <span
                                className={`chip ${
                                  r.state === 'OPEN'
                                    ? 'OPEN'
                                    : r.state === 'RESOLVED'
                                      ? 'RESOLVED'
                                      : 'CLOSED'
                                }`}
                              >
                                {r.state === 'OPEN'
                                  ? 'На проверке'
                                  : r.state === 'RESOLVED'
                                    ? 'Меры приняты'
                                    : 'Отклонена'}
                              </span>
                            </td>
                            <td className="muted" data-label="Дата" style={{ fontSize: 12 }}>
                              {dt(r.createdAt)}
                            </td>
                          </tr>
                        );
                      })}
                      {!reportsData?.items?.length && (
                        <tr>
                          <td colSpan={4} className="muted" style={{ textAlign: 'center', padding: 28 }}>
                            Жалоб по выбранному фильтру нет
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="card">
              {!activeReport ? (
                <div className="muted" style={{ textAlign: 'center', padding: 40 }}>
                  Выберите жалобу слева для проверки профиля и принятия мер
                </div>
              ) : (
                <>
                  <div
                    className="row"
                    style={{
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      gap: 8,
                      marginBottom: 14,
                    }}
                  >
                    <div style={{ minWidth: 0, flex: '1 1 200px' }}>
                      <h2 style={{ margin: 0, overflowWrap: 'anywhere' }}>
                        Жалоба #{activeReport.reportNumber} ·{' '}
                        {REASON_LABELS[activeReport.reason] ?? activeReport.reason}
                      </h2>
                      <div className="muted" style={{ fontSize: 12.5, marginTop: 4, overflowWrap: 'anywhere' }}>
                        Подал: <strong>{activeReport.reporterDisplayName ?? 'Пользователь'}</strong>{' '}
                        {activeReport.reporterUsername ? `(@${activeReport.reporterUsername})` : ''} · Источник:{' '}
                        <strong>{CONTEXT_LABELS[activeReport.context] ?? activeReport.context}</strong>
                      </div>
                    </div>
                    <button
                      type="button"
                      className="btn sm ghost"
                      onClick={() =>
                        navigate(`/users?userId=${encodeURIComponent(activeReport.targetUserId)}`)
                      }
                    >
                      Открыть профиль
                    </button>
                  </div>

                  {/* Snapshot vs Live Profile Comparison */}
                  <div className="report-compare-grid">
                    <div className="report-profile-box">
                      <div className="muted" style={{ fontSize: 11.5, marginBottom: 8 }}>
                        Снимок на момент жалобы
                      </div>
                      <div className="user-cell">
                        <UserAvatar
                          photoUrl={activeReport.snapshotPhotoUrl}
                          name={activeReport.snapshotName}
                          size={42}
                        />
                        <div>
                          <div style={{ fontWeight: 600 }}>
                            {activeReport.snapshotName ?? 'Без имени'}
                          </div>
                          <div className="muted mono" style={{ fontSize: 12 }}>
                            {activeReport.snapshotUsername
                              ? `@${activeReport.snapshotUsername}`
                              : `#${activeReport.targetTelegramId ?? ''}`}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="report-profile-box">
                      <div className="muted" style={{ fontSize: 11.5, marginBottom: 8 }}>
                        Текущий профиль в базе
                      </div>
                      <div className="user-cell">
                        <UserAvatar
                          photoUrl={activeReport.targetPhotoUrl}
                          customPhotoUrl={activeReport.targetCustomPhotoUrl}
                          name={activeReport.targetCustomName ?? activeReport.targetFirstName}
                          size={42}
                        />
                        <div>
                          <div className="user-cell-name-row">
                            <span style={{ fontWeight: 600 }}>
                              {activeReport.targetCustomName ??
                                activeReport.targetFirstName ??
                                'Без имени'}
                            </span>
                            {activeReport.targetIsBanned && (
                              <span className="chip banned">BAN</span>
                            )}
                          </div>
                          <div className="muted" style={{ fontSize: 11.5 }}>
                            TG имя: {activeReport.targetFirstName ?? '—'} · Кастом аватар:{' '}
                            {activeReport.targetCustomPhotoUrl ? 'Да' : 'Нет'}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {activeReport.comment && (
                    <div
                      className="report-profile-box"
                      style={{ marginBottom: 14, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}
                    >
                      <div className="muted" style={{ fontSize: 11.5, marginBottom: 4 }}>
                        Комментарий отправителя жалобы
                      </div>
                      <div>{activeReport.comment}</div>
                    </div>
                  )}

                  <AttachmentGallery
                    attachments={activeReport.attachments}
                    title={`Скриншоты к жалобе (${activeReport.attachments?.length ?? 0})`}
                  />

                  {activeReport.resolutionAction && (
                    <div className="report-profile-box" style={{ marginBottom: 14 }}>
                      <div style={{ fontWeight: 600 }}>
                        Решение оператора: {activeReport.resolutionAction}
                      </div>
                      <div className="muted" style={{ fontSize: 12, marginTop: 3 }}>
                        {activeReport.resolutionNote ?? 'Без примечания'} ·{' '}
                        {dt(activeReport.resolvedAt)}
                      </div>
                    </div>
                  )}

                  <label className="field-label">Примечание модератора (в аудит):</label>
                  <input
                    style={{ width: '100%', marginBottom: 14 }}
                    placeholder="Например: Сброшен оскорбительный никнейм…"
                    value={reportNote}
                    onChange={(e) => setReportNote(e.target.value)}
                  />

                  <div className="row" style={{ gap: 8 }}>
                    <button
                      type="button"
                      className="btn sm primary"
                      disabled={resolveReportMut.isPending}
                      onClick={() =>
                        resolveReportMut.mutate({ id: activeReport.id, action: 'RESET_NAME' })
                      }
                    >
                      <AdminIcon name="reset" size={13} />
                      <span>Сбросить ник</span>
                    </button>
                    <button
                      type="button"
                      className="btn sm"
                      disabled={resolveReportMut.isPending}
                      onClick={() =>
                        resolveReportMut.mutate({ id: activeReport.id, action: 'RESET_AVATAR' })
                      }
                    >
                      <AdminIcon name="reset" size={13} />
                      <span>Удалить аватарку</span>
                    </button>
                    <button
                      type="button"
                      className="btn sm"
                      disabled={resolveReportMut.isPending}
                      onClick={() =>
                        resolveReportMut.mutate({ id: activeReport.id, action: 'RESET_BOTH' })
                      }
                    >
                      Сбросить оба
                    </button>
                    <button
                      type="button"
                      className="btn sm danger"
                      disabled={resolveReportMut.isPending}
                      onClick={() =>
                        resolveReportMut.mutate({ id: activeReport.id, action: 'BAN_USER' })
                      }
                    >
                      <AdminIcon name="ban" size={13} />
                      <span>Заблокировать</span>
                    </button>
                    <button
                      type="button"
                      className="btn sm ghost"
                      disabled={resolveReportMut.isPending}
                      onClick={() =>
                        resolveReportMut.mutate({ id: activeReport.id, action: 'DISMISS' })
                      }
                    >
                      Отклонить
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </>
      ) : (
        <>
          <div className="toolbar">
            <div className="tabs" style={{ marginBottom: 0 }}>
              {TICKET_STATES.map((s) => (
                <button
                  key={s}
                  type="button"
                  className={`tab-btn ${stateFilter === s ? 'active' : ''}`}
                  onClick={() => setStateFilter(s)}
                >
                  {s === 'ALL' ? 'Все статусы' : s}
                </button>
              ))}
            </div>
            <select
              style={{ flex: '0 1 200px', minWidth: 0 }}
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
            >
              {CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {c === 'ALL' ? 'Все категории' : c}
                </option>
              ))}
            </select>
            <input
              style={{ flex: '1 1 220px', minWidth: 0 }}
              placeholder="Поиск по теме / @username…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>

          <div className="grid cols-2" style={{ alignItems: 'start' }}>
            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
              {isLoading ? (
                <div className="muted" style={{ padding: 20 }}>
                  Загрузка тикетов…
                </div>
              ) : (
                <div className="table-wrap">
                  <table className="mobile-cards">
                    <thead>
                      <tr>
                        <th>Пользователь</th>
                        <th>Тема / Категория</th>
                        <th>Статус</th>
                        <th>Обновлён</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(data?.items ?? []).map((t) => (
                        <tr
                          key={t.id}
                          style={{
                            cursor: 'pointer',
                            background: activeId === t.id ? 'var(--accent-soft)' : undefined,
                          }}
                          onClick={() => setActiveId(t.id)}
                        >
                          <td data-label="Пользователь">
                            <div style={{ fontWeight: 600 }}>{t.firstName ?? 'Пользователь'}</div>
                            <div className="muted mono" style={{ fontSize: 11 }}>
                              {t.username ? `@${t.username}` : `#${t.telegramId}`}
                            </div>
                          </td>
                          <td data-label="Тема / Категория">
                            <div style={{ fontWeight: 600 }}>{t.subject}</div>
                            <div>
                              <span className="chip info" style={{ marginTop: 4 }}>
                                {t.category}
                              </span>
                              <AttachmentCountBadge count={t.filesCount ?? 0} />
                            </div>
                          </td>
                          <td data-label="Статус">
                            <span className={`chip ${t.state}`}>{t.state}</span>
                          </td>
                          <td className="muted" data-label="Обновлён" style={{ fontSize: 12 }}>
                            {dt(t.lastMessageAt)}
                          </td>
                        </tr>
                      ))}
                      {!data?.items?.length && (
                        <tr>
                          <td colSpan={4} className="muted" style={{ textAlign: 'center', padding: 28 }}>
                            Тикетов по фильтру нет
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="card">
              {!activeId || !ticketDetail ? (
                <div className="muted" style={{ textAlign: 'center', padding: 40 }}>
                  Выберите тикет слева для просмотра диалога и ответа пользователю
                </div>
              ) : (
                <>
                  <div
                    className="row"
                    style={{
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      gap: 8,
                      marginBottom: 12,
                    }}
                  >
                    <div style={{ minWidth: 0, flex: '1 1 200px' }}>
                      <h2 style={{ margin: 0, overflowWrap: 'anywhere' }}>{ticketDetail.subject}</h2>
                      <div className="muted" style={{ fontSize: 12, marginTop: 4, overflowWrap: 'anywhere' }}>
                        Пользователь: {ticketDetail.firstName ?? '—'}{' '}
                        {ticketDetail.username
                          ? `@${ticketDetail.username}`
                          : `#${ticketDetail.telegramId}`}{' '}
                        ·{' '}
                        <button
                          type="button"
                          className="btn sm ghost"
                          style={{ padding: '2px 8px' }}
                          onClick={() =>
                            navigate(`/users?userId=${encodeURIComponent(ticketDetail.userId)}`)
                          }
                        >
                          Открыть профиль
                        </button>
                      </div>
                    </div>
                    <span className={`chip ${ticketDetail.state}`}>{ticketDetail.state}</span>
                  </div>

                  <div
                    style={{
                      maxHeight: 340,
                      overflowY: 'auto',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 8,
                      padding: 12,
                      background: 'var(--bg-2)',
                      borderRadius: 10,
                      marginBottom: 14,
                    }}
                  >
                    {(ticketDetail.messages ?? []).map((m) => {
                      const isUser = m.authorRole === 'USER';
                      const isNote = m.authorRole === 'INTERNAL_NOTE';
                      return (
                        <div
                          key={m.id}
                          style={{
                            alignSelf: isUser ? 'flex-start' : 'flex-end',
                            maxWidth: 'min(88%, 100%)',
                            padding: '9px 12px',
                            borderRadius: 10,
                            background: isNote
                              ? 'rgba(245, 158, 11, 0.16)'
                              : isUser
                                ? 'rgba(255,255,255,0.06)'
                                : 'rgba(130, 148, 255, 0.2)',
                            border: isNote
                              ? '1px dashed rgba(245, 158, 11, 0.45)'
                              : '1px solid var(--hair)',
                          }}
                        >
                          <div
                            className="row"
                            style={{ justifyContent: 'space-between', gap: 6, fontSize: 11, marginBottom: 4 }}
                          >
                            <strong
                              style={{
                                color: isNote ? '#fcd34d' : 'var(--ink-soft)',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 5,
                              }}
                            >
                              {isNote ? (
                                <>
                                  <AdminIcon name="lock" size={12} />
                                  <span>Внутренняя заметка</span>
                                </>
                              ) : (
                                m.authorDisplayName
                              )}
                            </strong>
                            <span className="muted">{dt(m.createdAt)}</span>
                          </div>
                          <div style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', fontSize: 13 }}>
                            {m.body}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <AttachmentGallery
                    attachments={ticketDetail.attachments}
                    title={`Фото-вложения к тикету (${ticketDetail.attachments?.length ?? 0})`}
                  />

                  <label className="muted" style={{ display: 'block', fontSize: 12, marginBottom: 4 }}>
                    Ответ пользователю (отправится в Telegram-бот):
                  </label>
                  <textarea
                    rows={3}
                    style={{ width: '100%', minWidth: 0, marginBottom: 10 }}
                    placeholder="Здравствуйте! Мы проверили ваш заказ…"
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                  />

                  <label className="muted" style={{ display: 'block', fontSize: 12, marginBottom: 4 }}>
                    Внутренняя заметка для операторов:
                  </label>
                  <input
                    style={{ width: '100%', minWidth: 0, marginBottom: 12 }}
                    placeholder="Опциональная заметка внутри тикета…"
                    value={internalNote}
                    onChange={(e) => setInternalNote(e.target.value)}
                  />

                  <div className="row" style={{ justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
                    <div className="row" style={{ gap: 8, flexWrap: 'wrap', flex: '1 1 220px', minWidth: 0 }}>
                      <span className="muted" style={{ fontSize: 12 }}>
                        Статус после ответа:
                      </span>
                      <select
                        style={{ flex: '1 1 150px', minWidth: 0 }}
                        value={nextState}
                        onChange={(e) => setNextState(e.target.value as typeof nextState)}
                      >
                        <option value="WAITING_USER">WAITING_USER</option>
                        <option value="IN_PROGRESS">IN_PROGRESS</option>
                        <option value="RESOLVED">RESOLVED</option>
                        <option value="CLOSED">CLOSED</option>
                      </select>
                    </div>
                    <button
                      type="button"
                      className="btn primary"
                      disabled={(!replyText.trim() && !internalNote.trim()) || replyMut.isPending}
                      onClick={() => replyMut.mutate()}
                    >
                      <AdminIcon name="send" size={13} />
                      <span>{replyMut.isPending ? 'Отправка…' : 'Отправить ответ'}</span>
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </>
      )}
    </>
  );
  // END_BLOCK_SUPPORT_TICKETS
}
